import React from "react";
import { useQueryClient } from "@tanstack/react-query";

import { GRID_ACTION_TYPES, GridActionType, Module } from "../../assets/types";
import { useSnackbar } from "../../assets/vfx/snackbar/vfxSnackbarContext";
import { SettingService } from "./settingService";
import { isGridActionType } from "../../assets/utils/helpers";
import { Constants } from "../../assets/utils/lib/constants";
import { MessageSeverity } from "../../assets/utils/lib/messageSeverity";
import { SettingForm } from "./settingForm";
import { SettingPage } from "./settingPage";
import { SettingView } from "./settingView";
import { CommunityService } from "../community/communityService";
import { apiErrorMessage } from "../../assets/utils/lib/error";
import { SettingDto as SystemSettingDto } from "../../services/dto/system/settingDto";
import { SettingDto as AccountSettingDto } from "../../services/dto/account/settingDto";
import { SettingDto as AdminSettingDto } from "../../services/dto/admin/settingDto";
import { SettingDto as PopHubSettingDto } from "../../services/dto/popHub/settingDto";
import { SettingDto as QuestionnaireSettingDto } from "../../services/dto/questionnaire/settingDto";
import { SettingDto as CommunityResourceSettingDto } from "../../services/dto/communityResource/settingDto";
import { useActionFromQuery } from "../../hooks/useActionFromQuery";

export type SettingTypeDto =
  | SystemSettingDto
  | AccountSettingDto
  | PopHubSettingDto
  | QuestionnaireSettingDto
  | AdminSettingDto
  | CommunityResourceSettingDto;

interface SettingTypeProps<T> {
  moduleType: Module;
}

export const SettingContainer = <T extends SettingTypeDto>(
  props: React.PropsWithoutRef<SettingTypeProps<T>>
) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const [data, setData] = React.useState<T>();
  const [drawerType, setDrawerType] = React.useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );

  const service: SettingService<T> = new SettingService<T>(props.moduleType);
  const settingQuery = service.getSettingQuery(data?.id ?? undefined);

  const isCommunityModule: boolean = [
    Module.Admin,
    Module.PopHub,
    Module.Questionnaire,
    Module.Community_Resource
  ].includes(props.moduleType);
  const communitiesQuery = isCommunityModule
    ? CommunityService.getCommunitiesQuery()
    : undefined;

  const { mutateAsync: saveSetting } = service.saveSetting();
  const { mutate: deleteSetting } = service.deleteSetting();
  const isEditMode: boolean = !!(data?.id && data.id !== Constants.emptyGuid);

  const closeDrawer = async () => {
      setDrawerType(GRID_ACTION_TYPES.HIDDEN);
      clearSearchAction();
  }

  const refreshGrid = (): void => {
    isEditMode && settingQuery?.refetch();
    queryClient.invalidateQueries({ queryKey: ["settingGridQuery"] });
    closeDrawer();
  };

  const handleApiResponse = (response: any, successMessage: string): void => {
    const errorMsg: string | null = apiErrorMessage(response.errorMessages);
    if (errorMsg) {
      showSnackbar(errorMsg, MessageSeverity.Error);
    } else {
      showSnackbar(successMessage, MessageSeverity.Success);
      refreshGrid();
    }
  };

  const handleSubmit = async (values: T) => {
    const message: string = isEditMode ? "Setting updated" : "Setting Saved";
    values.id = isEditMode ? data!.id : Constants.emptyGuid;
    return await saveSetting(values, {
      onSuccess: (response) => handleApiResponse(response, message),
    });
  };

  const handleDelete = (): void => {
    deleteSetting(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response) => handleApiResponse(response, "Setting deleted"),
    });
  };

  const handleEdit = (): void => {
    setData(settingQuery?.data);
    setDrawerType(GRID_ACTION_TYPES.EDIT);
    setSearchAction(GRID_ACTION_TYPES.EDIT, settingQuery.data?.id);
  };

  const handleCustomEvent = (actionType: string, dataId: string): void => {
    if (isGridActionType(actionType)) {
      setDrawerType(actionType);
      setData({ id: dataId } as T);
      setSearchAction(actionType, dataId);
    }
  };

  const { setSearchAction, clearSearchAction } = useActionFromQuery(handleCustomEvent);

  const renderForm = (isEdit: boolean): React.JSX.Element => (
    <SettingForm<T>
      show={isEdit ? !!settingQuery?.data : true}
      loading={isEdit ? settingQuery?.isLoading : undefined}
      data={isEdit ? settingQuery?.data : undefined}
      moduleType={props.moduleType}
      communities={communitiesQuery?.data}
      onHide={closeDrawer}
      onSave={handleSubmit}
    />
  );

  const renderDrawerContent = (): React.JSX.Element | null => {
    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW:
        return renderForm(false);
      case GRID_ACTION_TYPES.EDIT:
        return renderForm(true);
      case GRID_ACTION_TYPES.VIEW:
      case GRID_ACTION_TYPES.DOUBLE_CLICK:
        return (
          <SettingView<T>
            data={settingQuery?.data}
            show={!!settingQuery?.data}
            communities={communitiesQuery?.data}
            onHide={closeDrawer}
            onDelete={handleDelete}
            onEdit={handleEdit}
          />
        );
      default:
        return null;
    }
  };

  const renderSettingPage: boolean =
    [Module.Account, Module.System].includes(props.moduleType) ||
    Boolean(communitiesQuery?.data);

  return (
    <>
      {renderSettingPage && (
        <SettingPage<T>
          moduleType={props.moduleType}
          communities={communitiesQuery?.data}
          onCustomEvent={handleCustomEvent}
        />
      )}
      {renderDrawerContent()}
    </>
  );
};
